/**
 * FACTORING_DEMO — временная модалка для обучения на проде.
 * Включается флагом REACT_APP_FACTORING_DEMO=1 (пересборка фронта обязательна).
 * Заявки в банк НЕ уходят, API не дергается.
 * Вырезать целиком: grep -n FACTORING_DEMO crm_frontend/src
 */
import React, { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  MenuItem,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { useSelector } from "react-redux";
import { useCustomSnackbar } from "../../../utils/hooks/useCustomSnackbar";

const BRANCH_OPTIONS = [
  { code: "200000", city: "Алматы" },
  { code: "300000", city: "Астана" },
  { code: "230000", city: "Талдыкорган" },
  { code: "400000", city: "Актобе" },
  { code: "410000", city: "Актау" },
  { code: "420000", city: "Атырау" },
  { code: "430000", city: "Уральск" },
  { code: "500000", city: "Павлодар" },
  { code: "510000", city: "Петропавловск" },
  { code: "600000", city: "Караганда" },
  { code: "700000", city: "Шымкент" },
  { code: "710000", city: "Тараз" },
  { code: "720000", city: "Кызылорда" },
  { code: "800000", city: "Кокшетау" },
  { code: "810000", city: "Костанай" },
  { code: "900000", city: "Усть-Каменогорск" },
  { code: "910000", city: "Семей" },
];

const PERIOD_OPTIONS = [3, 6, 12, 18, 24];
const MIN_AMOUNT = 5000;
const MAX_AMOUNT = 3000000;

const STATUS_CONFIG = {
  WAITING_SIGN: { label: "На подписи у клиента", color: "#7C3AED", bg: "#F5F3FF" },
  IN_PROGRESS: { label: "На рассмотрении банка", color: "#F59E0B", bg: "#FFFBEB" },
  APPROVED: { label: "Одобрено", color: "#10B981", bg: "#ECFDF5" },
};

const DEMO_DOCS = [
  { name: "anketa", title: "Анкета-заявление" },
  { name: "dogovor", title: "Договор факторинга" },
];

const statusMeta = (code) =>
  STATUS_CONFIG[code] || { label: code, color: "#64748B", bg: "#F8FAFC" };

const digitsOnly = (v) => String(v || "").replace(/\D/g, "");

const fmtMoney = (v) =>
  Number(v || 0).toLocaleString("ru-RU", { maximumFractionDigits: 0 });

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const FactoringDemoModal = () => {
  const classes = useStyles();
  const { addDefaultSnack, addCloseSnack } = useCustomSnackbar();
  const { dealCreateData } = useSelector((state) => state.appState);

  const dealTotalPrice = Number(dealCreateData?.total_price) || 0;

  const [tab, setTab] = useState("apply");
  const [iin, setIin] = useState("");
  const [phone, setPhone] = useState(
    () => dealCreateData?.phone_number || dealCreateData?.phone || dealCreateData?.prop_phone || ""
  );
  const [amount, setAmount] = useState(() => {
    const seed = dealTotalPrice || 500000;
    return String(Math.min(MAX_AMOUNT, Math.max(MIN_AMOUNT, seed)));
  });
  const [period, setPeriod] = useState(12);
  const [branchCode, setBranchCode] = useState("200000");

  const [step, setStep] = useState("form");
  const [busy, setBusy] = useState(false);
  const [application, setApplication] = useState(null);
  const [history, setHistory] = useState([]);

  const validate = () => {
    if (digitsOnly(iin).length !== 12) return "ИИН должен содержать 12 цифр";
    const phoneDigits = digitsOnly(phone);
    if (!(phoneDigits.length === 11 && phoneDigits.startsWith("7"))) {
      return "Телефон в формате +7XXXXXXXXXX";
    }
    const principal = Number(amount);
    if (!principal) return "Укажите сумму займа";
    if (principal < MIN_AMOUNT || principal > MAX_AMOUNT) {
      return `Сумма от ${fmtMoney(MIN_AMOUNT)} до ${fmtMoney(MAX_AMOUNT)} ₸`;
    }
    return null;
  };

  const handlePrepare = async () => {
    const error = validate();
    if (error) {
      addCloseSnack(error, "error", 4000);
      return;
    }
    setBusy(true);
    await wait(700);
    const next = {
      id: 1,
      credit_contract: "FCT-DEMO-0001",
      principal: Number(amount),
      period: Number(period),
      status: "WAITING_SIGN",
      branch_code: branchCode,
      documents: DEMO_DOCS.map((doc) => ({ ...doc, signed: false })),
    };
    setApplication(next);
    setStep("sign");
    setBusy(false);
    addDefaultSnack("Документы подготовлены (демо)");
  };

  const copySignUrl = (doc) => {
    navigator.clipboard.writeText(`https://demo.nca.kz/sign/${doc.name}`).then(() => {
      addDefaultSnack("Ссылка скопирована (демо)");
    });
  };

  const handleCheckSign = async () => {
    setBusy(true);
    await wait(600);
    setApplication((prev) => ({
      ...prev,
      documents: (prev?.documents || []).map((doc) => ({ ...doc, signed: true })),
    }));
    setBusy(false);
    addDefaultSnack("Все документы подписаны (демо)");
  };

  const handleSubmit = async () => {
    setBusy(true);
    await wait(800);
    const submitted = {
      ...application,
      status: "IN_PROGRESS",
    };
    setApplication(submitted);
    setStep("bank");
    addDefaultSnack("Заявка отправлена в банк (демо)");
    await wait(1200);
    const approved = { ...submitted, status: "APPROVED" };
    setApplication(approved);
    setHistory((prev) => [approved, ...prev]);
    setStep("done");
    setBusy(false);
    addDefaultSnack("Заявка одобрена (демо)");
  };

  const handleReset = () => {
    setStep("form");
    setApplication(null);
  };

  const renderDocRow = (doc) => (
    <Box key={doc.name} className={classes.docRow}>
      <Box>
        <Typography className={classes.docTitle}>{doc.title}</Typography>
        <Typography className={classes.muted}>
          {doc.signed ? "Подписан" : "Ожидает подписи клиента"}
        </Typography>
      </Box>
      <Box className={classes.docActions}>
        {doc.signed ? (
          <Button size="small" variant="outlined" disabled>
            Подписанный PDF
          </Button>
        ) : (
          <>
            <Button size="small" variant="outlined" onClick={() => copySignUrl(doc)}>
              Копировать ссылку
            </Button>
            <Button size="small" color="primary" disabled>
              Открыть
            </Button>
          </>
        )}
      </Box>
    </Box>
  );

  const renderForm = () => (
    <>
      <Box className={classes.row}>
        <TextField
          label="ИИН"
          variant="outlined"
          size="small"
          value={iin}
          onChange={(e) => setIin(digitsOnly(e.target.value).slice(0, 12))}
          fullWidth
        />
        <TextField
          label="Телефон"
          variant="outlined"
          size="small"
          placeholder="+77001234567"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          fullWidth
        />
      </Box>
      <Box className={classes.row}>
        <TextField
          label="Сумма займа"
          variant="outlined"
          size="small"
          value={amount}
          onChange={(e) => {
            const next = Number(digitsOnly(e.target.value) || 0);
            if (!next) {
              setAmount("");
              return;
            }
            setAmount(String(Math.min(MAX_AMOUNT, next)));
          }}
          helperText={`От ${fmtMoney(MIN_AMOUNT)} до ${fmtMoney(MAX_AMOUNT)} ₸`}
          fullWidth
        />
        <TextField
          select
          label="Срок, мес."
          variant="outlined"
          size="small"
          value={period}
          onChange={(e) => setPeriod(Number(e.target.value))}
          fullWidth
        >
          {PERIOD_OPTIONS.map((p) => (
            <MenuItem key={p} value={p}>
              {p}
            </MenuItem>
          ))}
        </TextField>
      </Box>
      <Box className={classes.row}>
        <TextField
          select
          label="Филиал банка"
          variant="outlined"
          size="small"
          value={branchCode}
          onChange={(e) => setBranchCode(e.target.value)}
          fullWidth
        >
          {BRANCH_OPTIONS.map((b) => (
            <MenuItem key={b.code} value={b.code}>
              {b.city} ({b.code})
            </MenuItem>
          ))}
        </TextField>
      </Box>
      <Box className={classes.actions}>
        <Button
          variant="contained"
          color="primary"
          disabled={busy}
          onClick={handlePrepare}
          className={classes.submitBtn}
        >
          {busy ? <CircularProgress size={22} color="inherit" /> : "Отправить на подпись"}
        </Button>
      </Box>
    </>
  );

  const renderSign = () => {
    const allSigned =
      application?.documents?.length > 0 &&
      application.documents.every((doc) => doc.signed);
    const st = statusMeta(application.status);
    return (
      <>
        <Box className={classes.signBlock}>
          <Box className={classes.cardHead}>
            <Typography className={classes.sectionTitle}>Печатные формы на ЭЦП</Typography>
            <span className={classes.status} style={{ color: st.color, background: st.bg }}>
              {st.label}
            </span>
          </Box>
          <Typography className={classes.muted}>
            Договор {application.credit_contract} · {fmtMoney(application.principal)} ₸ ·{" "}
            {application.period} мес.
          </Typography>
          <Typography className={classes.muted}>
            Отправьте клиенту ссылки на подпись — так же, как ЭЦП на сделке
          </Typography>
          {application.documents.map(renderDocRow)}
        </Box>
        <Box className={classes.actions}>
          <Button variant="outlined" disabled={busy} onClick={handleCheckSign}>
            {busy ? <CircularProgress size={22} color="inherit" /> : "Проверить подписи"}
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={busy || !allSigned}
            onClick={handleSubmit}
            className={classes.submitBtn}
          >
            {busy ? <CircularProgress size={22} color="inherit" /> : "Отправить в банк"}
          </Button>
        </Box>
      </>
    );
  };

  const renderBank = () => {
    const st = statusMeta(application.status);
    return (
      <Box className={classes.statusCard}>
        <Box className={classes.cardHead}>
          <Typography className={classes.cardTitle}>
            #{application.id} · {application.credit_contract}
          </Typography>
          <span className={classes.status} style={{ color: st.color, background: st.bg }}>
            {st.label}
          </span>
        </Box>
        <Typography className={classes.muted}>
          {fmtMoney(application.principal)} ₸ · {application.period} мес.
        </Typography>
        {busy && (
          <Box className={classes.center}>
            <CircularProgress size={24} />
          </Box>
        )}
      </Box>
    );
  };

  const renderDone = () => {
    const st = statusMeta(application.status);
    return (
      <>
        <Box className={classes.statusCard}>
          <Box className={classes.cardHead}>
            <Typography className={classes.cardTitle}>
              #{application.id} · {application.credit_contract}
            </Typography>
            <span className={classes.status} style={{ color: st.color, background: st.bg }}>
              {st.label}
            </span>
          </Box>
          <Typography className={classes.muted}>
            {fmtMoney(application.principal)} ₸ · {application.period} мес.
          </Typography>
        </Box>
        <Box className={classes.actions}>
          <Button variant="outlined" color="primary" onClick={handleReset}>
            Пройти сценарий заново
          </Button>
        </Box>
      </>
    );
  };

  return (
    <Box className={classes.root}>
      <Box className={classes.demoBanner}>
        ДЕМО-режим обучения · заявки в банк не отправляются
      </Box>
      <Box className={classes.tabs}>
        <button
          type="button"
          className={tab === "apply" ? `${classes.tab} ${classes.tabActive}` : classes.tab}
          onClick={() => setTab("apply")}
        >
          Заявка
        </button>
        <button
          type="button"
          className={tab === "history" ? `${classes.tab} ${classes.tabActive}` : classes.tab}
          onClick={() => setTab("history")}
        >
          История
          {history.length > 0 && <span className={classes.badge}>{history.length}</span>}
        </button>
      </Box>

      {tab === "apply" && (
        <Box className={classes.form}>
          {step === "form" && renderForm()}
          {step === "sign" && renderSign()}
          {step === "bank" && renderBank()}
          {step === "done" && renderDone()}
        </Box>
      )}

      {tab === "history" && (
        <Box className={classes.history}>
          {history.length === 0 && (
            <Typography className={classes.muted}>Заявок пока нет — пройдите сценарий</Typography>
          )}
          {history.map((item) => {
            const st = statusMeta(item.status);
            return (
              <Box key={`${item.id}-${item.status}`} className={classes.card}>
                <Box className={classes.cardHead}>
                  <Typography className={classes.cardTitle}>
                    #{item.id} · {item.credit_contract}
                  </Typography>
                  <span className={classes.status} style={{ color: st.color, background: st.bg }}>
                    {st.label}
                  </span>
                </Box>
                <Typography className={classes.muted}>
                  {fmtMoney(item.principal)} ₸ · {item.period} мес.
                </Typography>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default FactoringDemoModal;

const useStyles = makeStyles({
  root: {
    width: 640,
    maxWidth: "100%",
    display: "flex",
    flexDirection: "column",
  },
  demoBanner: {
    background: "#FEF3C7",
    color: "#92400E",
    border: "1px solid #FCD34D",
    borderRadius: 8,
    padding: "8px 12px",
    fontSize: 13,
    fontWeight: 600,
    marginBottom: 12,
  },
  tabs: {
    display: "flex",
    gap: 4,
    borderBottom: "2px solid #E4E8F5",
    marginBottom: 16,
  },
  tab: {
    border: "none",
    background: "transparent",
    padding: "10px 14px",
    cursor: "pointer",
    fontWeight: 600,
    color: "#64748B",
    borderBottom: "2px solid transparent",
    marginBottom: -2,
  },
  tabActive: {
    color: "#0F172A",
    borderBottomColor: "#4361EE",
  },
  badge: {
    marginLeft: 6,
    background: "#4361EE",
    color: "#fff",
    borderRadius: 10,
    padding: "1px 7px",
    fontSize: 11,
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  row: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 12,
  },
  sectionTitle: {
    fontWeight: 600,
  },
  statusCard: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    padding: 12,
    borderRadius: 8,
    background: "#F8FAFC",
    border: "1px solid #E2E8F0",
  },
  signBlock: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    padding: 12,
    borderRadius: 8,
    background: "#F8FAFC",
    border: "1px solid #E2E8F0",
  },
  docRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 8,
    padding: "8px 0",
    borderTop: "1px solid #E2E8F0",
  },
  docTitle: {
    fontWeight: 600,
    marginBottom: 4,
  },
  docActions: {
    display: "flex",
    gap: 6,
    flexShrink: 0,
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    flexWrap: "wrap",
    gap: 8,
    marginTop: 8,
  },
  submitBtn: {
    minWidth: 180,
    textTransform: "none",
    fontWeight: 700,
    background: "linear-gradient(90deg, #7C3AED 0%, #2563EB 100%)",
  },
  history: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  card: {
    border: "1px solid #E2E8F0",
    borderRadius: 10,
    padding: 12,
    background: "#fff",
  },
  cardHead: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 8,
    marginBottom: 4,
  },
  cardTitle: {
    fontWeight: 700,
  },
  status: {
    fontSize: 12,
    fontWeight: 700,
    borderRadius: 999,
    padding: "2px 8px",
  },
  center: {
    display: "flex",
    justifyContent: "center",
    padding: 16,
  },
  muted: {
    color: "#64748B",
    fontSize: 13,
  },
});
